import { useState } from 'react';

interface SearchFiltersProps {
  onSearch: (filters: any) => void;
  logTypeOptions: string[];
  moduleOptions: string[];
  operationTypeOptions: string[];
}

export default function SearchFilters({
  onSearch,
  logTypeOptions,
  moduleOptions,
  operationTypeOptions
}: SearchFiltersProps) {
  const [operatorQuery, setOperatorQuery] = useState('');
  const [ip, setIp] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [timeRange, setTimeRange] = useState<[string, string]>(['', '']);
  const [selectedLogTypes, setSelectedLogTypes] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [selectedOperationTypes, setSelectedOperationTypes] = useState<string[]>([]);

  const handleSearch = () => {
    onSearch({
      operatorQuery,
      ip,
      timeRange,
      logTypes: selectedLogTypes,
      modules: selectedModules,
      operationTypes: selectedOperationTypes
    });
  };

  const handleReset = () => {
    setOperatorQuery('');
    setIp('');
    setTimeRange(['', '']);
    setSelectedLogTypes([]);
    setSelectedModules([]);
    setSelectedOperationTypes([]);
    onSearch({
      operatorQuery: '',
      ip: '',
      timeRange: ['', ''],
      logTypes: [],
      modules: [],
      operationTypes: []
    });
  };

  const toggleSelection = (value: string, selected: string[], setter: (val: string[]) => void) => {
    if (selected.includes(value)) {
      setter(selected.filter(item => item !== value));
    } else {
      setter([...selected, value]);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 mb-5">
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            操作员ID/名称
          </label>
          <input
            type="text"
            value={operatorQuery}
            onChange={(e) => setOperatorQuery(e.target.value)}
            placeholder="请输入操作员ID或名称"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">IP地址</label>
          <input
            type="text"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            placeholder="请输入IP地址"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="col-span-2 flex items-end gap-3">
          <button
            onClick={handleSearch}
            className="px-5 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-search-line mr-2"></i>
            搜索
          </button>
          <button
            onClick={handleReset}
            className="px-5 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line mr-2"></i>
            重置
          </button>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="px-5 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className={`ri-${showAdvanced ? 'arrow-up' : 'arrow-down'}-s-line mr-2`}></i>
            {showAdvanced ? '收起' : '展开'}筛选
          </button>
        </div>
      </div>

      {showAdvanced && (
        <div className="pt-4 border-t border-gray-200 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                操作时间
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={timeRange[0]}
                  onChange={(e) => setTimeRange([e.target.value, timeRange[1]])}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <span className="text-gray-500">至</span>
                <input
                  type="datetime-local"
                  value={timeRange[1]}
                  onChange={(e) => setTimeRange([timeRange[0], e.target.value])}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">日志类型</label>
            <div className="flex flex-wrap gap-2">
              {logTypeOptions.map((type) => (
                <button
                  key={type}
                  onClick={() => toggleSelection(type, selectedLogTypes, setSelectedLogTypes)}
                  className={`px-4 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    selectedLogTypes.includes(type)
                      ? 'bg-teal-100 text-teal-700 border border-teal-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">模块</label>
            <div className="flex flex-wrap gap-2">
              {moduleOptions.map((module) => (
                <button
                  key={module}
                  onClick={() => toggleSelection(module, selectedModules, setSelectedModules)}
                  className={`px-4 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    selectedModules.includes(module)
                      ? 'bg-teal-100 text-teal-700 border border-teal-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {module}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">操作类型</label>
            <div className="flex flex-wrap gap-2">
              {operationTypeOptions.map((type) => (
                <button
                  key={type}
                  onClick={() => toggleSelection(type, selectedOperationTypes, setSelectedOperationTypes)}
                  className={`px-4 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    selectedOperationTypes.includes(type)
                      ? 'bg-teal-100 text-teal-700 border border-teal-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
